import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { inputStyle, modalStyle, headerStyle } from "../OrderEntry/styles";
import { useAuth } from "../../Context/AuthProvider";
import { createCounterparty, updateCounterparty } from "../../http/api";
import { tokens, withAlpha } from '../../Common/designTokens';

const AddCounterpartyDialog = ({
  open,
  onClose,
  onAdd,
  // New props for edit support (backward-compatible defaults)
  mode = "add", // "add" | "edit"
  initialData = null, // { counterPartyMstId, counterPartyName, description }
  onSubmit, // optional callback(newItem) for both modes
}) => {
  const { token } = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState(false);
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Initialize form based on mode
      if (mode === "edit" && initialData) {
        setName(initialData.counterPartyName || "");
        setDescription(initialData.description || "");
      } else {
        setName("");
        setDescription("");
      }
      setNameError(false);
      setApiError("");
      setSubmitting(false);
    }
  }, [open, mode, initialData]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError(true);
      return;
    }
    setApiError("");
    setSubmitting(true);
    try {
      const payload = {
        counterPartyName: name.trim(),
        description: description.trim(),
      };
      let result;
      if (mode === "edit" && initialData?.counterPartyMstId) {
        result = await updateCounterparty(token, initialData.counterPartyMstId, payload);
      } else {
        result = await createCounterparty(token, payload);
      }
      // created/updated shape (on success): { success:true, counterPartyMstId, counterPartyName, description, ... }
      if (onSubmit) onSubmit(result);
      if (onAdd && mode === "add" && result?.counterPartyMstId && result?.counterPartyName) {
        onAdd(result); // keep backward compat only when entity payload is present
      }
      // parent controls dialog closing
    } catch (err) {
      setApiError(err?.message || (mode === "edit" ? "Failed to update counterparty" : "Failed to create counterparty"));
    } finally {
      setSubmitting(false);
    }
  };

  const dialogTitle = mode === "edit" ? "Edit Counterparty" : "Add Counterparty";
  const submitLabel = submitting ? (mode === "edit" ? "Updating..." : "Adding...") : (mode === "edit" ? "Update" : "Add");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          ...modalStyle,
          width: 520,
          maxWidth: "95vw",
          p: 0,
          borderRadius: "8px", // ensure 8px radius
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          ...headerStyle,
          p: 2,
          backgroundColor: tokens.colors.defaultGridRowColor,
          color: tokens.colors.fontColour,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "inherit" }}>
          {dialogTitle}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="close"
          sx={{ color: tokens.colors.fontColour85 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2, backgroundColor: "rgba(33,33,33,1)" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, backgroundColor: tokens.colors.defaultGridRowColor }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError && e.target.value.trim()) setNameError(false);
              if (apiError) setApiError("");
            }}
            variant="filled"
            fullWidth
            error={nameError}
            helperText={nameError ? "Name is required" : ""}
            sx={inputStyle}
            InputProps={{ disableUnderline: true }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => {
              const v = e.target.value.slice(0, 512);
              setDescription(v);
              if (apiError) setApiError("");
            }}
            variant="filled"
            fullWidth
            multiline
            minRows={2}
            sx={{
              ...inputStyle,
              "& .MuiFilledInput-root": {
                ...(inputStyle?.["& .MuiFilledInput-root"] || {}),
                paddingLeft: "0px",
              },
            }}
            InputProps={{ disableUnderline: true }}
            inputProps={{ maxLength: 512 }}
          />
        </Box>

        {/* Character counter */}
        <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ color: tokens.palette.danger, fontSize: "0.85rem" }}>
            {apiError}
          </Typography>
          <Typography sx={{ color: tokens.colors.fontColour85, fontSize: "0.75rem" }}>
            {description.length}/512
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.06)", backgroundColor: "rgba(33,33,33,1)" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none", fontWeight: 700, px: 3, borderRadius: "8px"}}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            borderRadius: "8px",
            backgroundColor: tokens.palette.primary,
            color: tokens.colors.white,
            "&:hover": { backgroundColor: tokens.palette.primaryA80 },
            "&.Mui-disabled": {
              backgroundColor: tokens.colors.disabledBackground,
              color: tokens.colors.disabledFontColour,
              boxShadow: "none",
              cursor: "not-allowed",
            },
          }}
          disabled={!name.trim() || submitting}
          aria-disabled={!name.trim() || submitting}
          title={!name.trim() ? "Enter a name to enable" : undefined}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCounterpartyDialog;
